import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';

export function normalizeInstructions(value) {
  const summary = typeof value?.summary === 'string' ? value.summary.trim() : '';
  const steps = Array.isArray(value?.steps)
    ? value.steps.map((s) => String(s ?? '').trim()).filter(Boolean)
    : [];
  if (!summary && !steps.length) return null;
  return { summary, steps };
}

export function hasInstructions(value) {
  return !!normalizeInstructions(value);
}

export function instructionsStepCount(value) {
  return normalizeInstructions(value)?.steps?.length || 0;
}

/**
 * Modal to edit route guidance: general summary + free-form step list.
 */
export default function CityGeoInstructionsDialog({
  visible,
  onHide,
  value,
  onSave,
  routeLabel = 'this route',
}) {
  const [summary, setSummary] = useState('');
  const [steps, setSteps] = useState(['']);

  useEffect(() => {
    if (!visible) return;
    const next = normalizeInstructions(value);
    setSummary(next?.summary || '');
    setSteps(next?.steps?.length ? [...next.steps] : ['']);
  }, [visible, value]);

  const canSave = useMemo(() => {
    if (summary.trim()) return true;
    return steps.some((s) => String(s).trim());
  }, [summary, steps]);

  const updateStep = (index, text) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? text : s)));
  };

  const addStep = () => setSteps((prev) => [...prev, '']);

  const removeStep = (index) => {
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [''];
    });
  };

  const moveStep = (index, delta) => {
    setSteps((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const handleSave = () => {
    onSave?.(normalizeInstructions({ summary, steps }));
    onHide?.();
  };

  const handleClear = () => {
    onSave?.(null);
    onHide?.();
  };

  const footer = (
    <div className="flex justify-content-between align-items-center flex-wrap gap-2 w-full">
      <Button
        type="button"
        label="Clear all"
        icon="pi pi-trash"
        severity="danger"
        text
        onClick={handleClear}
        disabled={!hasInstructions(value) && !canSave}
      />
      <div className="flex gap-2">
        <Button type="button" label="Cancel" icon="pi pi-times" severity="secondary" text onClick={onHide} />
        <Button
          type="button"
          label="Save instructions"
          icon="pi pi-check"
          className="p-button-primary primary-button"
          onClick={handleSave}
          disabled={!canSave && !hasInstructions(value)}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      header={`Instructions · ${routeLabel}`}
      visible={visible}
      onHide={onHide}
      style={{ width: 'min(560px, 94vw)' }}
      modal
      dismissableMask
      footer={footer}
      className="city-geo-instructions-dialog"
    >
      <p className="text-600 text-sm mt-0 mb-3">
        Users see this when they pick the lane. Keep it short and actionable — entrance tips,
        documents, and what to expect in line.
      </p>

      <label className="font-semibold text-sm block mb-1" htmlFor="city-geo-instr-summary">
        General instructions
      </label>
      <InputTextarea
        id="city-geo-instr-summary"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        rows={4}
        autoResize
        className="w-full mb-3"
        placeholder="General instructions"
      />

      <div className="flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <label className="font-semibold text-sm m-0">Step by step</label>
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add step"
          size="small"
          severity="secondary"
          outlined
          onClick={addStep}
        />
      </div>

      <div className="flex flex-column gap-2">
        {steps.map((step, index) => (
          <div key={`step-${index}`} className="flex align-items-start gap-2">
            <span
              className="flex align-items-center justify-content-center border-circle font-bold text-sm flex-shrink-0"
              style={{
                width: 28,
                height: 28,
                marginTop: 6,
                background: '#E8EEF9',
                color: '#2020A0',
              }}
            >
              {index + 1}
            </span>
            <InputText
              value={step}
              onChange={(e) => updateStep(index, e.target.value)}
              className="w-full"
              placeholder={`Step ${index + 1}`}
            />
            <div className="flex flex-column gap-1">
              <Button
                type="button"
                icon="pi pi-arrow-up"
                size="small"
                text
                rounded
                disabled={index === 0}
                onClick={() => moveStep(index, -1)}
                aria-label="Move up"
              />
              <Button
                type="button"
                icon="pi pi-arrow-down"
                size="small"
                text
                rounded
                disabled={index === steps.length - 1}
                onClick={() => moveStep(index, 1)}
                aria-label="Move down"
              />
            </div>
            <Button
              type="button"
              icon="pi pi-times"
              size="small"
              text
              rounded
              severity="danger"
              onClick={() => removeStep(index)}
              aria-label="Remove step"
              disabled={steps.length <= 1 && !String(step).trim()}
            />
          </div>
        ))}
      </div>
    </Dialog>
  );
}
